<template>
  <v-dialog
    v-model="dialog"
    max-width="500"
    persistent
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-send</v-icon>
        Enviar {{ coin }}
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" @click="dialog = false"></v-btn>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="isFormValid" @submit.prevent="handleSubmit">
          <!-- Campo de endereço do destinatário -->
          <v-text-field
            v-model="recipient"
            label="Endereço do destinatário"
            variant="outlined"
            :rules="[
              v => !!v || 'Endereço é obrigatório',
              v => isValidQuarnXAddress(v) || 'Endereço QuarnX inválido. Deve começar com Q'
            ]"
            hint="Endereço QuarnX que receberá os tokens"
            persistent-hint
            class="mb-4"
            prepend-inner-icon="mdi-account"
            clearable
          ></v-text-field>

          <!-- Dropdown para escolher rede -->
          <v-select
            v-model="selectedNetwork"
            :items="networks"
            item-title="name"
            item-value="id"
            label="Rede"
            variant="outlined"
            class="mb-4"
            return-object
            prepend-inner-icon="mdi-ethereum"
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="24" :color="item.raw.color" class="mr-2">
                  <span class="text-caption text-white">{{ item.raw.icon }}</span>
                </v-avatar>
                {{ item.raw.name }}
              </div>
            </template>
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-avatar size="24" :color="item.raw.color">
                    <span class="text-caption text-white">{{ item.raw.icon }}</span>
                  </v-avatar>
                </template>
                <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-select>

          <!-- Campo de valor -->
          <v-text-field
            v-model="amount"
            :label="t('dex.amount')"
            variant="outlined"
            :rules="[
              v => !!v || 'Valor é obrigatório',
              v => !isNaN(parseFloat(v)) && parseFloat(v) > 0 || 'Valor deve ser maior que 0',
              v => parseFloat(v) <= availableBalance || 'Valor excede o saldo disponível'
            ]"
            type="number"
            step="0.000001"
            min="0.000001"
            class="mb-4"
            prepend-inner-icon="mdi-currency-kin"
          >
            <template v-slot:append>
              <v-btn
                variant="text"
                color="primary"
                size="small"
                @click="setMaxAmount"
              >
                MAX
              </v-btn>
              <span class="text-caption ml-2">{{ coin }}</span>
            </template>
          </v-text-field>

          <!-- Campo memo/descrição (opcional) -->
          <v-text-field
            v-model="memo"
            label="Memo (opcional)"
            variant="outlined"
            hint="Descrição ou referência para esta transação"
            persistent-hint
            class="mb-4"
            prepend-inner-icon="mdi-text"
            maxlength="100"
            counter
          ></v-text-field>

          <!-- Informações de resumo -->
          <v-card variant="outlined" class="mb-4">
            <v-card-text>
              <div class="text-subtitle-2 mb-2">Resumo da transação</div>
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption">Valor a enviar:</span>
                <span class="text-body-2">{{ amount || 0 }} {{ coin }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption">Taxa:</span>
                <span class="text-body-2">{{ estimatedFee }} {{ coin }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="d-flex justify-space-between font-weight-medium">
                <span>Total:</span>
                <span>{{ calculateTotal() }} {{ coin }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-form>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn
          variant="tonal"
          block
          color="primary"
          size="large"
          :disabled="!isFormValid || isLoading"
          :loading="isLoading"
          @click="handleSubmit"
        >
          Enviar {{ coin }}
        </v-btn>
      </v-card-actions>
    </v-card>
    
    <!-- Diálogo de confirmação -->
    <v-dialog
      v-model="showConfirmation"
      max-width="400"
    >
      <v-card>
        <v-card-title class="bg-primary text-white">
          Confirmar transação
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1 mb-3">Você está enviando:</p>
          <div class="text-h5 text-center font-weight-bold mb-3">{{ amount }} {{ coin }}</div>
          <p class="text-body-1 mb-1">Para o endereço:</p>
          <div class="address-box pa-2 mb-3">{{ formatAddress(recipient) }}</div>
          <p class="text-caption text-medium-emphasis mt-2">Por favor, confirme que estas informações estão corretas antes de prosseguir.</p>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="showConfirmation = false"
          >
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            @click="confirmSend"
            :loading="isLoading"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Diálogo de sucesso -->
    <v-dialog
      v-model="showSuccess"
      max-width="400"
    >
      <v-card>
        <v-card-text class="pt-4 text-center">
          <v-icon
            icon="mdi-check-circle"
            color="success"
            size="64"
            class="mb-4"
          ></v-icon>
          <h3 class="text-h5 mb-3">Transação enviada!</h3>
          <p class="mb-4">Sua transação foi enviada com sucesso e está aguardando confirmação na blockchain.</p>
          <div class="transaction-hash mb-4">
            <div class="text-caption mb-1">Hash da transação:</div>
            <div class="transaction-hash-value">{{ transactionHash }}</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="closeSuccessDialog"
          >
            Fechar
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { useDexStore } from '@/stores/dex'
import { useAppStore } from '@/stores/app'

export default {
  name: 'SendKinXDialog',
  
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue'],
  
  data() {
    return {
      dialog: this.modelValue,
      recipient: '',
      amount: '',
      memo: '',
      selectedNetwork: null,
      isFormValid: false,
      isLoading: false,
      showConfirmation: false,
      showSuccess: false,
      transactionHash: '',
      networks: [
        { id: 'quarnx', name: 'QuarnX', color: 'primary', icon: 'Q' }
      ],
      estimatedFee: 0.0001
    }
  },
  
  computed: {
    dexStore() {
      return useDexStore()
    },
    appStore() {
      return useAppStore()
    },
    availableBalance() {
      return this.dexStore.wallet?.balance || 0
    }
  },
  
  watch: {
    modelValue(val) {
      this.dialog = val
    },
    dialog(val) {
      if (!val) this.resetForm()
      this.$emit('update:modelValue', val)
    }
  },
  
  methods: {
    isValidQuarnXAddress(address) {
      // Validação básica de endereço QuarnX (começa com Q e tem comprimento apropriado)
      return address && address.startsWith('Q') && address.length >= 30 && address.length <= 45
    },
    
    setMaxAmount() {
      if (this.availableBalance > this.estimatedFee) {
        this.amount = (this.availableBalance - this.estimatedFee).toFixed(6)
      } else {
        this.amount = '0'
        this.appStore.showSnackbar({
          text: 'Saldo insuficiente para pagar a taxa',
          color: 'error'
        })
      }
    },
    
    calculateTotal() {
      const amountValue = parseFloat(this.amount) || 0
      return (amountValue + parseFloat(this.estimatedFee)).toFixed(6)
    },
    
    handleSubmit() {
      if (!this.$refs.form.validate()) return
      
      // Mostrar diálogo de confirmação
      this.showConfirmation = true
    },
    
    confirmSend() {
      this.isLoading = true
      this.showConfirmation = false
      
      // Simular uma transação na blockchain
      setTimeout(() => {
        // Gerar um hash de transação fictício
        this.transactionHash = 'pendente...'
        
        // Atualizar saldo (simulação)
        const newBalance = this.dexStore.wallet.balance - parseFloat(this.amount) - this.estimatedFee
        this.dexStore.updateBalance(newBalance)
        
        this.isLoading = false
        this.showSuccess = true
      }, 2000)
    },
    
    closeSuccessDialog() {
      this.showSuccess = false
      this.dialog = false
    },
    
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
      this.recipient = ''
      this.amount = ''
      this.memo = ''
      this.selectedNetwork = this.networks[0]
      this.showConfirmation = false
      this.showSuccess = false
      this.transactionHash = ''
    },
    
    formatAddress(address) {
      if (!address) return ''
      return address.length > 16 
        ? `${address.substring(0, 8)}...${address.substring(address.length - 8)}`
        : address
    }
  },
  
  mounted() {
    // Definir a rede padrão como QuarnX
    this.selectedNetwork = this.networks[0]
  }
}
</script>

<style scoped>
.address-box {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  word-break: break-all;
  font-family: monospace;
  font-size: 14px;
}

.transaction-hash {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  padding: 12px;
}

.transaction-hash-value {
  font-family: monospace;
  word-break: break-all;
  font-size: 14px;
}
</style>
